import { useState } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'

interface CreateViewViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onViewCreated: () => void
  duplicateFrom?: {
    originalName: string
    newName: string
    definition: unknown
  }
}

// Utility function to extract view definition from response
function extractViewDefinition(definition: unknown): string {
  if (!definition) return ''
  
  console.log('Extracting view definition from:', definition)
  
  if (typeof definition === 'string') {
    console.log('Using string definition directly')
    return definition
  }
  
  if (definition && typeof definition === 'object') {
    const def = definition as { definition?: unknown; view_definition?: unknown }
    if ('definition' in def && typeof def.definition === 'string') {
      console.log('Using definition from response object')
      return def.definition
    }
    if ('view_definition' in def && typeof def.view_definition === 'string') {
      console.log('Using view_definition from response object')
      return def.view_definition
    }
  }
  
  console.log('Could not extract view definition')
  return ''
}

export function CreateViewView({ connection, database, schema, onViewCreated, duplicateFrom }: CreateViewViewProps) {
  const [formData, setFormData] = useState(() => {
    if (duplicateFrom?.definition) {
      try {
        const extractedDefinition = extractViewDefinition(duplicateFrom.definition)
        console.log('Initializing with duplicated view data:', {
          name: duplicateFrom.newName,
          definition: `${extractedDefinition.substring(0, 100)}...`
        })
        return {
          name: duplicateFrom.newName || '',
          definition: extractedDefinition,
          replace: false
        }
      } catch (error) {
        console.error('Error processing duplicated view definition:', error)
        return {
          name: duplicateFrom.newName || '',
          definition: '',
          replace: false
        }
      }
    }
    return {
      name: '',
      definition: '',
      replace: false
    }
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { showToast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim() || !formData.definition.trim()) {
      showToast('error', 'Please fill in all required fields')
      return
    }

    try {
      setIsSubmitting(true)
      
      // Create view using agent-proxy
      await AgentProxyService.createView(
        connection.id,
        database,
        {
          view_name: formData.name,
          view_definition: formData.definition,
          replace: formData.replace
        },
        schema
      )
      
      showToast('success', `View "${formData.name}" created successfully`)
      onViewCreated()
    } catch (error) {
      console.error('Failed to create view:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to create view'
      showToast('error', errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    onViewCreated()
  }

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {duplicateFrom ? `Duplicate View: ${duplicateFrom.originalName}` : 'Create New View'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {duplicateFrom 
              ? `Create a copy of "${duplicateFrom.originalName}" with modifications in ${database}`
              : `Create a virtual table based on a SQL SELECT statement in ${database}`
            }
          </p>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              View Configuration
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* View Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  View Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => { setFormData({ ...formData, name: e.target.value }) }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="my_view"
                  required
                />
              </div>

              {/* Schema (for PostgreSQL) */}
              {connection.type === 'postgresql' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Schema
                  </label>
                  <input
                    type="text"
                    value={schema || 'public'}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                  />
                </div>
              )}

              {/* Replace Option */}
              <div className="lg:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="replace"
                    checked={formData.replace}
                    onChange={(e) => { setFormData({ ...formData, replace: e.target.checked }) }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="replace" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Replace if view already exists
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* View Definition */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              View Definition
            </h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                SQL SELECT Statement *
              </label>
              <textarea
                value={formData.definition}
                onChange={(e) => { setFormData({ ...formData, definition: e.target.value }) }}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                placeholder="SELECT column1, column2&#10;FROM table_name&#10;WHERE condition"
                required
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Enter the SELECT statement that defines your view. Do not include CREATE VIEW syntax.
              </p>
            </div>
          </div>

          {/* Example */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
              Example View Definition
            </h3>
            <div className="bg-blue-100 dark:bg-blue-900/40 rounded-lg p-4">
              <pre className="text-sm text-blue-800 dark:text-blue-300 font-mono whitespace-pre-wrap">
{`SELECT 
    u.id,
    u.name,
    u.email,
    COUNT(o.id) as order_count
FROM users u
LEFT JOIN orders o ON u.id = o.user_id
WHERE u.active = true
GROUP BY u.id, u.name, u.email`}
              </pre>
            </div>
            <p className="mt-3 text-sm text-blue-800 dark:text-blue-300">
              This creates a view showing active users with their order counts.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating View...
                </>
              ) : (
                'Create View'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
